import { Title, Text, Container, Button, Stack, Box, SimpleGrid, Group, ThemeIcon, rem } from '@mantine/core';
import { IconRocket } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import StatsStrip from '../info/StatsStrip';
import { HOME_CONTENT } from '../../data/homeContent';

const ANIM_DURATION = 0.6;
const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: { y: 0, opacity: 1, transition: { duration: ANIM_DURATION, ease: "easeOut" } }
};

const MotionTitle = motion(Title);
const MotionText = motion(Text);
const MotionButton = motion(Button);
const MotionDiv = motion.div;

/**
 * HeroFeature component displays a single feature highlight with an icon and description.
 * @param {Object} props - Component properties.
 * @param {React.ElementType} props.icon - Icon component to display.
 * @param {string} props.title - Title of the feature.
 * @param {string} props.description - Short description of the feature.
 */
function HeroFeature({ icon: Icon, title, description }) {
  return (
    <Group
        wrap="nowrap"
        gap="sm"
        align="center"
        justify="center"
        style={{ textAlign: 'left', width: '100%', maxWidth: '250px' }}
    >
      <ThemeIcon size="lg" radius="md" variant="light" color="blue" style={{ flexShrink: 0 }}>
        <Icon style={{ width: rem(20), height: rem(20) }} stroke={1.5} />
      </ThemeIcon>
      <Stack gap={0}>
        <Text size="sm" fw={700} lh={1.2} c="white">{title}</Text>
        <Text size="xs" c="dimmed" lh={1.2}>{description}</Text>
      </Stack>
    </Group>
  );
}

/**
 * Hero component that serves as the main entry point of the landing page.
 * Displays the primary value proposition and call-to-action.
 * @param {Object} props - Component properties.
 * @param {Function} props.onLaunch - Function to trigger the bot launch process.
 */
export default function Hero({ onLaunch }) {
    return (
        <Container size="lg" py={{ base: 60, md: 90 }}>
            <motion.div
              initial="hidden"
              animate="visible"
              transition={{ staggerChildren: 0.1 }}
            >
                <Stack align="center" gap="xl">

                    <Stack gap="xs" align="center" style={{ textAlign: 'center' }}>
                        <MotionTitle
                            variants={itemVariants}
                            order={1}
                            size={60}
                            fw={900}
                            variant="gradient"
                            gradient={{ from: 'blue', to: 'cyan', deg: 90 }}
                            style={{
                                lineHeight: 1.1,
                                letterSpacing: '-1px'
                            }}
                        >
                            {HOME_CONTENT.hero.title}
                        </MotionTitle>
                        <MotionText
                            variants={itemVariants}
                            size="xl" c="dimmed" maw={600}
                        >
                            {HOME_CONTENT.hero.subtitle}
                        </MotionText>
                    </Stack>

                    <MotionButton
                        variants={itemVariants}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={onLaunch}
                        size="xl"
                        radius="xl"
                        variant="gradient"
                        gradient={{ from: 'indigo', to: 'cyan' }}
                        leftSection={<IconRocket size={24} />}
                        style={{ boxShadow: '0 0 30px rgba(34, 139, 230, 0.4)' }}
                    >
                        {HOME_CONTENT.hero.cta}
                    </MotionButton>

                    <MotionDiv
                        variants={itemVariants}
                        style={{
                            width: '100%',
                            maxWidth: '900px',
                            marginTop: '40px',
                            display: 'flex',
                            justifyContent: 'center'
                        }}
                    >
                        <SimpleGrid
                            cols={{ base: 1, sm: 2, lg: 4 }}
                            spacing={{ base: 20, md: 30 }}
                            style={{ width: '100%' }}
                        >
                            {HOME_CONTENT.hero.features.map((feat, i) => (
                                <Box key={i} style={{ display: 'flex', justifyContent: 'center' }}>
                                    <HeroFeature icon={feat.icon} title={feat.title} description={feat.desc} />
                                </Box>
                            ))}
                        </SimpleGrid>
                    </MotionDiv>

                    <motion.div
                        variants={itemVariants}
                        style={{ marginTop: '50px' }}
                    >
                        <Box
                            style={{
                                background: 'rgba(10, 10, 10, 0.7)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                borderRadius: '50px',
                                padding: '12px 40px',
                                width: 'fit-content',
                                margin: '0 auto',
                                boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
                            }}
                        >
                            <StatsStrip />
                        </Box>
                    </motion.div>

                </Stack>
            </motion.div>
        </Container>
    );
}